import { useState, useRef, useCallback, useMemo, useId } from 'react';

import { useTranslation } from 'react-i18next';

import { normalizeOption, SELECT_ALL_VALUE } from './utils';

import { usePositioning, POSITIONING_STRATEGY } from '../_hooks/usePositioning';
import { useDebounce } from '../../../Hooks/useDebounce';
import useScrollClose from '../_hooks/useScrollClose';

import { ComboBoxInput } from './_components/ComboBoxInput';
import { ComboBoxDropdown } from './_components/ComboBoxDropdown';
import Chip from '../Chip/Chip';

import './ComboBox.css';

/**
 * ComboBox - Searchable dropdown with smart context-aware rendering
 *
 * @param {Array} options - Array of options (strings, numbers, or {value, label} objects)
 * @param {*} value - Currently selected value (single) or array of values (multi-select)
 * @param {Function} onChange - Callback(value, option) when selection changes
 * @param {string} label - Field label text
 * @param {string} placeholder - Input placeholder text
 * @param {boolean} disabled - Disable the combobox
 * @param {string} className - Additional CSS classes
 * @param {Function} renderOption - Custom option renderer(option, {isHighlighted, isSelected})
 * @param {string} strategy - Rendering strategy: 'auto' | 'dialog' | 'modal' | 'portal'. Set for better performance.
 * @param {string} id - Optional ID for the component
 * @param {string} valueKey - Property name for option value (default: 'value')
 * @param {string} labelKey - Property name for option label (default: 'label')
 * @param {boolean} isClearable - Show clear button when value is selected (default: false)
 * @param {Function} onClear - Optional callback when clear button is clicked
 * @param {boolean} required - Mark field as required (default: false)
 * @param {boolean} isMulti - Enable multi-select mode (default: false)
 * @param {string} multiPlaceholder - Placeholder shown in multi-select when items are already selected
 * @param {boolean} hideInternalChips - Hide chips and show count summary in placeholder (default: false)
 * @param {boolean} showSelectAll - Multi-select only: show a "Select all" row that toggles every option matching the search (default: false)
 */
const ComboBox = ({options = [], value, onChange, label, placeholder, disabled = false, className = '', renderOption, strategy = POSITIONING_STRATEGY.AUTO, id, valueKey = 'value', labelKey = 'label', isClearable = false, onClear, required = false, isMulti = false, multiPlaceholder, hideInternalChips = false, showSelectAll = false}) => {
    const { t } = useTranslation();
    const generatedId = useId();
    const componentId = id || `scb-${generatedId}`;

    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');

    const inputRef = useRef(null);
    const userHasTypedRef = useRef(false);
    const dropdownRef = useRef(null);

    const positioning = usePositioning(inputRef, dropdownRef, isOpen, strategy);
    const debouncedSearchText = useDebounce(searchText, 150);

    const normalizedOptions = useMemo(() => {
        return options.map(opt => normalizeOption(opt, valueKey, labelKey));
    }, [options, valueKey, labelKey]);

    const selectedOption = useMemo(() => {
        if (isMulti) return null;
        return normalizedOptions.find((opt) => opt.value === value);
    }, [isMulti, normalizedOptions, value]);

    const selectedOptions = useMemo(() => {
        if (!isMulti) return [];
        const values = Array.isArray(value) ? value : [];
        return normalizedOptions.filter((opt) => values.includes(opt.value));
    }, [isMulti, normalizedOptions, value]);

    const filteredOptions = useMemo(() => {
        if (!userHasTypedRef.current || !debouncedSearchText) {
            return normalizedOptions;
        }
        
        const query = debouncedSearchText.toLowerCase();
        return normalizedOptions.filter((option) => {
            return option.label.toLowerCase().includes(query);
        });
    }, [normalizedOptions, debouncedSearchText]);

    const selectableFilteredOptions = useMemo(() => filteredOptions.filter((option) => !option.disabled), [filteredOptions]);

    const dropdownOptions = useMemo(() => {
        const showSelectAllRow = isMulti && showSelectAll && selectableFilteredOptions.length > 0;
        if (!showSelectAllRow) return filteredOptions;

        const values = new Set(Array.isArray(value) ? value : []);
        const selectedCount = selectableFilteredOptions.filter((option) => values.has(option.value)).length;
        const allSelected = selectedCount === selectableFilteredOptions.length;
        const indeterminate = selectedCount > 0 && !allSelected;
        const selectAllOption = {
            value: SELECT_ALL_VALUE,
            label: allSelected ? t('common.deselectAll') : t('common.selectAll'),
            isSelectAll: true,
            checked: allSelected,
            indeterminate,
        };
        return [selectAllOption, ...filteredOptions];
    }, [isMulti, showSelectAll, selectableFilteredOptions, filteredOptions, value, t]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setSearchText('');
        userHasTypedRef.current = false;
    }, []);

    const handleOpen = useCallback(() => {
        if (disabled) return;
        setIsOpen(true);
        userHasTypedRef.current = false;
        const label = isMulti ? '' : (selectedOption?.label || '');
        setSearchText(label);

        setTimeout(() => {
            inputRef.current?.select();
        }, 0);
    }, [disabled, selectedOption, isMulti]);

    useScrollClose(isOpen, dropdownRef, handleClose);

    const displayText = isOpen ? searchText : (isMulti ? '' : selectedOption?.label || '');

    const handleSelect = useCallback(
        (option) => {
            if (option && !option.disabled) {
                if (isMulti) {
                    const values = Array.isArray(value) ? value : [];
                    let newValues;
                    if (option.isSelectAll) {
                        // The search text is kept so the row flips to "Deselect all" and can undo the same set.
                        const visibleValues = new Set(selectableFilteredOptions.map((opt) => opt.value));
                        newValues = option.checked
                            ? values.filter((v) => !visibleValues.has(v))
                            : [...new Set([...values, ...visibleValues])];
                    } else {
                        newValues = values.includes(option.value)
                            ? values.filter(v => v !== option.value)
                            : [...values, option.value];
                        setSearchText('');
                    }
                    const newValueSet = new Set(newValues);
                    const newOptions = normalizedOptions.filter(opt => newValueSet.has(opt.value));
                    onChange?.(newValues, newOptions);
                } else {
                    onChange?.(option.value, option);
                    handleClose();
                }
            }
        },
        [isMulti, value, normalizedOptions, onChange, handleClose, selectableFilteredOptions]
    );

    const handleSearchChange = useCallback(
        (text) => {
            setSearchText(text);
            userHasTypedRef.current = true;
            if (!isOpen) setIsOpen(true);
        },
        [isOpen]
    );

    const handleClear = useCallback(() => {
        setSearchText('');
        
        if (isMulti) {
            onChange?.([], []);
        } else {
            onChange?.(null, null);
        }
        
        onClear?.();
        setIsOpen(false);
    }, [isMulti, onChange, onClear]);

    const handleRemoveChip = useCallback((optionValue) => {
        if (isMulti) {
            const values = Array.isArray(value) ? value : [];
            const newValues = values.filter(v => v !== optionValue);
            const newOptions = normalizedOptions.filter(opt => newValues.includes(opt.value));
            onChange?.(newValues, newOptions);
        }
    }, [isMulti, value, normalizedOptions, onChange]);

    const effectivePlaceholder = useMemo(() => {
        if (!isMulti || selectedOptions.length === 0) {
            return placeholder || '';
        }
        
        if (hideInternalChips) {
            return multiPlaceholder || `${selectedOptions.length} selected`;
        }
        
        return multiPlaceholder || placeholder || '';
    }, [isMulti, selectedOptions.length, placeholder, multiPlaceholder, hideInternalChips]);

    return (
        <div className={`skynet-combobox ${className}`.trim()}>
            <ComboBoxInput
                ref={inputRef}
                id={componentId}
                value={displayText}
                placeholder={effectivePlaceholder}
                disabled={disabled}
                isOpen={isOpen}
                onOpen={handleOpen}
                onClose={handleClose}
                onChange={handleSearchChange}
                label={label}
                isClearable={isClearable}
                selectedValue={value}
                onClear={handleClear}
                required={required}
            />
            {isMulti && !hideInternalChips && selectedOptions.length > 0 && (
                <div className="d-flex flex-wrap mt-8 gap-4">
                    {selectedOptions.map((option) => (
                        <Chip
                            key={option.value}
                            label={option.label}
                            onRemove={() => handleRemoveChip(option.value)}
                        />
                    ))}
                </div>
            )}
            <ComboBoxDropdown
                ref={dropdownRef}
                isOpen={isOpen}
                options={dropdownOptions}
                selectedValue={value}
                onSelect={handleSelect}
                onOpen={handleOpen}
                onClose={handleClose}
                positioning={positioning}
                renderOption={renderOption}
                inputRef={inputRef}
                anchorId={componentId}
                isMulti={isMulti}
            />
        </div>
    );
};

export default ComboBox;
